import { useCallback, useState, type FormEvent } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '@platform/core/lib/routes';
import { useAuthLoginMutation, useAuthOptionsQuery, useAuthSessionQuery } from '@platform/features/auth';
import { AuthField } from './AuthField';
import { AuthFrame } from './AuthFrame';
import { authErrorKey } from './authErrorKey';
import { authRoutes } from './authRoutes';
import { devPasswordStub } from './devPasswordStub';
import type { LoginStep } from './mfa/LoginStep';
import { MfaCodeStep } from './mfa/MfaCodeStep';
import { MfaEnrolStep } from './mfa/MfaEnrolStep';
import { isMfaCode } from './mfa/mfaCode';
import { SsoButtons } from './SsoButtons';
import { useEnterApp } from './useEnterApp';
import { useSsoSignIn } from './useSsoSignIn';

/** Відповіді сервера, що відкривають крок двоетапної перевірки: код (увімкнено TOTP) чи підключення застосунку (його ще немає). */
const NEXT_STEP: Record<string, LoginStep> = { mfa_required: 'code', mfa_setup_required: 'enrol' };

/**
 * Вхід (прототип v4 screen 'login'): «Увійти», «Демо-обліковий запис уже заповнено.», «Продовжити з Google / Microsoft», «або через ел.
 * пошту», e-mail і пароль, помилки «Обліковий запис із цією адресою не знайдено.» / «Неправильний пароль.» (Development; у Production —
 * одна загальна «Неправильна ел. пошта або пароль.», рішення 5 етапу 9), «Уперше тут? Створити обліковий запис». Способи входу, реєстрацію й демо-обліковий запис визначає сервер (GET /auth/options); у Development поля заповнено демо-обліковим
 * записом (після виходу — порожні, як signOutTop прототипу). Помилка повернення OIDC приходить як ?error=<код>. Двоетапна перевірка (поза
 * прототипом, рішення 4): mfa_required — крок 6-значного коду, mfa_setup_required — крок підключення застосунку автентифікації (QR-код),
 * обидва в рамці входу. Успіх — останній або перший простір (без просторів — створення простору).
 */
export function LoginPage() {
  const { t } = useTranslation();
  const { state } = useLocation();
  const [params] = useSearchParams();
  const signedOut = (state as { signedOut?: boolean } | null)?.signedOut === true;
  const { data: options, isError } = useAuthOptionsQuery();
  // сесію перечитано щоразу на вході: прострочена сесія спостерігача не лишає банера над формою
  useAuthSessionQuery(undefined, { refetchOnMountOrArgChange: true });
  const [login, { isLoading }] = useAuthLoginMutation();
  const enter = useEnterApp();
  const [step, setStep] = useState<LoginStep>('password');
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [error, setError] = useState(() => (params.get('error') ? t(authErrorKey(params.get('error'))) : ''));
  const onError = useCallback((c: string) => {
    const next = NEXT_STEP[c];
    // новий крок — без помилки; на відкритому кроці (хибний код) — текст помилки
    if (next && next !== step) { setStep(next); setError(''); } else setError(t(authErrorKey(c)));
  }, [t, step]);
  const restart = useCallback((code?: string) => { setStep('password'); setError(code ? t(authErrorKey(code)) : ''); }, [t]);
  const sso = useSsoSignIn(options, onError);
  const demo = !signedOut && options?.demoEmail ? options.demoEmail : '';
  const emailValue = email ?? demo;
  const passwordValue = password ?? (demo && options?.mode === 'dev' ? devPasswordStub : '');
  const edit = (set: (v: string) => void) => (v: string) => { set(v); setError(''); };
  const signIn = (mfaCode?: string) => {
    if (isLoading) return;
    login({ email: emailValue.trim().toLowerCase(), password: passwordValue, ...(mfaCode !== undefined ? { mfaCode } : {}) }).unwrap()
      .then(() => enter(routes.root()), (err: { code?: string }) => onError(err.code ?? ''));
  };
  const submit = (e: FormEvent) => { e.preventDefault(); signIn(); };
  const submitCode = (code: string) => { if (isMfaCode(code)) signIn(code); else setError(t('auth.errors.mfa_code_short')); };
  if (!options) return <AuthFrame><div className="au__form">{isError && <div className="au__error" role="alert">{t('auth.errors.failed')}</div>}</div></AuthFrame>;
  if (step === 'code') {
    return <AuthFrame><MfaCodeStep email={emailValue.trim()} busy={isLoading} error={error} onSubmit={submitCode} onEdit={() => setError('')} onBack={() => restart()} /></AuthFrame>;
  }
  if (step === 'enrol') return <AuthFrame><MfaEnrolStep email={emailValue.trim()} onDone={() => enter(routes.root())} onRestart={restart} /></AuthFrame>;
  const { providers } = options;
  return (
    <AuthFrame>
      <form className="au__form" onSubmit={submit} noValidate>
        <div className="au__head">
          <h1>{t('auth.signIn')}</h1>
          {options.demoEmail && <div className="au__sub">{t('auth.demoPrefilled')}</div>}
        </div>
        <SsoButtons providers={providers} onSso={sso} />
        {providers.password && (providers.google || providers.microsoft) && <div className="au__or">{t('auth.orEmail')}</div>}
        {providers.password && (
          <>
            <AuthField label={t('auth.email')} type="email" value={emailValue} placeholder={t('auth.emailPh')} autoComplete="username" onChange={edit(setEmail)} />
            <AuthField label={t('auth.password')} type="password" value={passwordValue} placeholder={t('auth.passwordPh')} autoComplete="current-password"
              onChange={edit(setPassword)} />
          </>
        )}
        {error && <div className="au__error" role="alert">{error}</div>}
        {providers.password && <button type="submit" className="au__submit" disabled={isLoading}>{t('auth.signIn')}</button>}
        {options.selfRegistration && (
          <div className="au__switch"><span>{t('auth.newHere')}</span><Link to={authRoutes.register()}>{t('auth.createAccountLink')}</Link></div>
        )}
      </form>
    </AuthFrame>
  );
}
